/**
 * useListeningTracker
 *
 * Self-contained client hook that maintains a server-side "listening
 * session" while the caller reports `isPlaying = true`. Sends a
 * heartbeat to /api/listening/heartbeat every 60s with the actual
 * elapsed wall-clock seconds, plus a final flush on stop/unmount.
 *
 *   useListeningTracker({
 *     isPlaying,
 *     contentId: track?.id,
 *     contentType: 'audiobook' | 'cinematic' | 'tts',
 *     title: track?.title,
 *   });
 *
 * The hook is a no-op while contentId is falsy. It auto-starts a new
 * session when contentId changes mid-play, so a user moving from one
 * episode to the next splits cleanly in the DB.
 */
import { useEffect, useRef } from 'react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const HEARTBEAT_INTERVAL_MS = 60_000;

const useListeningTracker = ({ isPlaying, contentId, contentType, title, speed = 1 }) => {
  // Session bookkeeping kept in refs so changes don't trigger re-renders.
  const sessionRef = useRef(null);
  const playStartRef = useRef(null);
  const accumulatedSecRef = useRef(0);
  const intervalRef = useRef(null);

  // Keep latest speed in a ref so the heartbeat picks up changes mid-session.
  const speedRef = useRef(speed);
  useEffect(() => { speedRef.current = speed; }, [speed]);

  // Send `delta_seconds` (since last flush) to the heartbeat endpoint.
  const sendHeartbeat = async () => {
    const sid = sessionRef.current;
    const delta = Math.round(accumulatedSecRef.current);
    if (!sid || delta <= 0) return;
    accumulatedSecRef.current = 0;
    try {
      await authFetch(`${BACKEND_URL}/api/listening/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sid,
          delta_seconds: delta,
          speed: speedRef.current || 1,
        }),
      });
    } catch {
      accumulatedSecRef.current += delta;
    }
  };

  const flushSession = async () => {
    const sid = sessionRef.current;
    if (!sid) return;
    const delta = Math.round(accumulatedSecRef.current);
    accumulatedSecRef.current = 0;
    sessionRef.current = null;
    try {
      await authFetch(`${BACKEND_URL}/api/listening/flush`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sid,
          delta_seconds: delta,
          speed: speedRef.current || 1,
        }),
      });
    } catch {
      /* swallow */
    }
  };

  const startSession = async () => {
    if (sessionRef.current || !contentId) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/api/listening/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content_id: contentId,
          content_type: contentType || 'audiobook',
          title: title || '',
        }),
      });
      if (r.ok) {
        const d = await r.json();
        sessionRef.current = d.session_id;
      }
    } catch {
      /* ignore — next isPlaying tick will retry */
    }
  };

  // Reset session whenever the tracked content changes.
  useEffect(() => {
    if (!sessionRef.current) return;
    // Flush old session, then a new one will start on the next play.
    flushSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);

  useEffect(() => {
    // Stop the heartbeat timer + capture any partial second on pause.
    const tick = () => {
      if (playStartRef.current) {
        accumulatedSecRef.current += (Date.now() - playStartRef.current) / 1000;
        playStartRef.current = Date.now();
      }
      sendHeartbeat();
    };

    if (isPlaying && contentId) {
      // Start session if we don't have one yet.
      if (!sessionRef.current) {
        startSession();
      }
      playStartRef.current = Date.now();
      intervalRef.current = setInterval(tick, HEARTBEAT_INTERVAL_MS);
    } else {
      // On pause, bank the partial second + send a heartbeat.
      if (playStartRef.current) {
        accumulatedSecRef.current += (Date.now() - playStartRef.current) / 1000;
        playStartRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Best-effort heartbeat on pause so we don't lose the tail.
      if (sessionRef.current) sendHeartbeat();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, contentId, contentType, title]);

  // Final flush on unmount — guarantees the session is closed even if
  // the user navigates away mid-playback.
  useEffect(() => {
    return () => {
      // Bank any in-flight time.
      if (playStartRef.current) {
        accumulatedSecRef.current += (Date.now() - playStartRef.current) / 1000;
        playStartRef.current = null;
      }
      flushSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useListeningTracker;
